import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import {
  OpcUaCertificateDTO,
  OpcUaX509UserRequest,
} from "../../serviceapi/dataCollectorApi";
import CertificateDetailsDialog from "./CertificateDetailsDialog";
import EnabledTag from "./EnabledTag";
import ThumbprintCell from "./ThumbprintCell";
import X509UserDialog from "./X509UserDialog";
import { certificateLabel, formatCreatedAt } from "./opcUaUtils";

interface X509UsersTabProps {
  x509Users: OpcUaCertificateDTO[];
  isLoading: boolean;
  isBusy: boolean;
  fetchCertificates: () => Promise<void>;
  createX509User: (request: OpcUaX509UserRequest) => Promise<boolean>;
  importX509User: (file: File, username?: string) => Promise<boolean>;
  deleteCertificate: (certificate: OpcUaCertificateDTO) => Promise<void>;
  downloadCertificate: (certificate: OpcUaCertificateDTO) => Promise<void>;
}

const X509UsersTab = ({
  x509Users,
  isLoading,
  isBusy,
  fetchCertificates,
  createX509User,
  importX509User,
  deleteCertificate,
  downloadCertificate,
}: X509UsersTabProps) => {
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const [isCreateDialogVisible, setIsCreateDialogVisible] = useState<boolean>(false);
  const [detailsCertificate, setDetailsCertificate] =
    useState<OpcUaCertificateDTO | null>(null);

  const onGlobalFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setGlobalFilterValue(value);
    setFilters((prevFilters) => ({
      ...prevFilters,
      global: { ...prevFilters.global, value },
    }));
  };

  const handleDelete = (certificate: OpcUaCertificateDTO) => {
    confirmDialog({
      message: `Delete X.509 user "${certificateLabel(
        certificate
      )}"? The identity and its certificate are removed and the client can no longer authenticate.`,
      header: "Delete X.509 user",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: () => deleteCertificate(certificate),
    });
  };

  const actionBodyTemplate = (certificate: OpcUaCertificateDTO) => (
    <div className="flex justify-content-end gap-1">
      <Button
        className="p-button-text p-button-sm"
        icon="pi pi-eye"
        tooltip="Details"
        onClick={() => setDetailsCertificate(certificate)}
      />
      <Button
        className="p-button-text p-button-sm"
        icon="pi pi-download"
        tooltip="Download .der"
        onClick={() => downloadCertificate(certificate)}
        disabled={!certificate.thumbprint}
      />
      <Button
        className="p-button-text p-button-danger p-button-sm"
        icon="pi pi-trash"
        tooltip="Delete"
        onClick={() => handleDelete(certificate)}
        disabled={!certificate.thumbprint || isBusy}
      />
    </div>
  );

  return (
    <div className="flex flex-column gap-3" style={{ height: "100%" }}>
      <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            type="search"
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Search X.509 users"
          />
        </IconField>
        <div className="flex gap-2">
          <Button
            label="Add X.509 user"
            icon="pi pi-plus"
            className="p-button-sm"
            onClick={() => setIsCreateDialogVisible(true)}
            disabled={isBusy}
          />
          <Button
            className="p-button-text p-button-sm"
            icon="pi pi-refresh"
            tooltip="Refresh"
            onClick={fetchCertificates}
            disabled={isLoading}
          />
        </div>
      </div>

      {/* loading is a prop, not a spinner swapped in for the table — see ApplicationCertificatesTab. */}
      <DataTable
        value={x509Users}
        loading={isLoading}
        dataKey="thumbprint"
        size="small"
        scrollable
        scrollHeight="flex"
        emptyMessage="No X.509 user identities registered."
        filters={filters}
        onFilter={(e) => setFilters(e.filters)}
        globalFilterFields={["username", "subject", "thumbprint"]}
        style={{ flex: 1, minHeight: 0 }}
      >
        <Column field="username" header="Username" sortable />
        <Column
          field="subject"
          header="Subject"
          sortable
          body={(certificate: OpcUaCertificateDTO) => (
            <span title={certificate.subject} className="block text-overflow-ellipsis overflow-hidden white-space-nowrap">
              {certificate.subject || "—"}
            </span>
          )}
          style={{ maxWidth: "16rem" }}
        />
        <Column
          header="Thumbprint"
          body={(certificate: OpcUaCertificateDTO) => (
            <ThumbprintCell thumbprint={certificate.thumbprint} />
          )}
          style={{ width: "15rem" }}
        />
        <Column
          field="enabled"
          header="Enabled"
          sortable
          body={(certificate: OpcUaCertificateDTO) => (
            <EnabledTag enabled={certificate.enabled} />
          )}
          style={{ width: "7rem" }}
        />
        <Column
          field="createdAt"
          header="Created"
          sortable
          body={(certificate: OpcUaCertificateDTO) =>
            formatCreatedAt(certificate.createdAt)
          }
          style={{ width: "12rem" }}
        />
        <Column body={actionBodyTemplate} style={{ width: "9rem" }} />
      </DataTable>

      {isCreateDialogVisible && (
        <X509UserDialog
          existingUsernames={x509Users
            .map((certificate) => certificate.username)
            .filter((username): username is string => !!username)}
          onIssue={createX509User}
          onImport={importX509User}
          close={() => setIsCreateDialogVisible(false)}
        />
      )}

      {detailsCertificate && (
        <CertificateDetailsDialog
          certificate={detailsCertificate}
          onDownload={downloadCertificate}
          close={() => setDetailsCertificate(null)}
        />
      )}
    </div>
  );
};

export default X509UsersTab;
